import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import usePrefersReducedMotion from "./motion/usePrefersReducedMotion";

/**
 * Up to two lines, as in Figma; anything longer shows its full name above the row.
 */
export default function SidebarTruncateTooltip({
  text,
  isDark,
  className = "",
}) {
  const ref = useRef(null);
  const tipRef = useRef(null);
  const anchorCenterX = useRef(0);
  const reduced = usePrefersReducedMotion();
  const [truncated, setTruncated] = useState(false);
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      setTruncated(el.scrollHeight > el.clientHeight + 1);
    };

    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [text]);

  const place = (el) => {
    if (!el || el.scrollHeight <= el.clientHeight + 1) {
      setCoords(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const tipW = tipRef.current?.offsetWidth || Math.min(240, Math.max(96, text.length * 7.2));
    const centerX = r.left + r.width / 2;
    anchorCenterX.current = centerX;
    const left = Math.max(10, Math.min(centerX - tipW / 2, window.innerWidth - tipW - 10));
    setCoords({
      bottom: window.innerHeight - r.top + 10,
      left,
      caretLeft: centerX - left,
    });
  };

  useLayoutEffect(() => {
    if (!coords || !tipRef.current) return;
    const tipW = tipRef.current.offsetWidth;
    const centerX = anchorCenterX.current;
    const left = Math.max(10, Math.min(centerX - tipW / 2, window.innerWidth - tipW - 10));
    if (Math.abs(left - coords.left) > 1 || Math.abs(centerX - left - coords.caretLeft) > 1) {
      setCoords((c) =>
        c
          ? {
              ...c,
              left,
              caretLeft: centerX - left,
            }
          : c
      );
    }
  }, [coords, text]);

  const hide = () => setCoords(null);

  return (
    <span className={`relative min-w-0 flex-1 ${className}`}>
      <span
        ref={ref}
        className="line-clamp-2 text-left"
        onMouseEnter={(e) => place(e.currentTarget)}
        onMouseLeave={hide}
        onFocus={(e) => place(e.currentTarget)}
        onBlur={hide}
      >
        {text}
      </span>
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {coords && truncated ? (
              <motion.div
                key="sidebar-tip"
                ref={tipRef}
                role="tooltip"
                className="pointer-events-none fixed z-[400] font-ds"
                style={{ bottom: coords.bottom, left: coords.left }}
                initial={reduced ? false : { opacity: 0, y: 8, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.94 }}
                transition={
                  reduced
                    ? { duration: 0.1 }
                    : { type: "spring", stiffness: 520, damping: 28, mass: 0.55 }
                }
              >
                <div
                  className={`relative max-w-[240px] overflow-hidden rounded-radius-sm px-3 py-2 text-caption-1-semibold leading-snug tracking-tight shadow-[0_12px_40px_-8px_rgba(0,0,0,0.55)] ${
                    isDark ? "text-slate-50" : "text-white"
                  }`}
                  style={{
                    background: isDark
                      ? "linear-gradient(145deg, #12201c 0%, #0c1210 55%, #101816 100%)"
                      : "linear-gradient(145deg, #0f1f1a 0%, #0a1210 55%, #102018 100%)",
                    boxShadow: isDark
                      ? "0 14px 36px -10px rgba(0,0,0,0.65), 0 0 0 1px rgba(114,234,213,0.22), 0 0 24px -6px rgba(114,234,213,0.35)"
                      : "0 14px 36px -10px rgba(0,0,0,0.45), 0 0 0 1px rgba(16,185,129,0.35), 0 0 20px -4px rgba(16,185,129,0.3)",
                  }}
                >
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-px"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(114,234,213,0.75), transparent)",
                    }}
                    aria-hidden
                  />
                  <div
                    className="pointer-events-none absolute -left-6 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full opacity-40 blur-2xl"
                    style={{ background: "rgba(114,234,213,0.55)" }}
                    aria-hidden
                  />
                  <span className="relative z-[1]">{text}</span>
                  <span
                    aria-hidden
                    className="absolute left-0 top-full h-0 w-0 -translate-x-1/2"
                    style={{
                      left: coords.caretLeft,
                      borderLeft: "6px solid transparent",
                      borderRight: "6px solid transparent",
                      borderTop: isDark
                        ? "7px solid #101816"
                        : "7px solid #102018",
                      filter: isDark
                        ? "drop-shadow(0 1px 0 rgba(114,234,213,0.25))"
                        : "drop-shadow(0 1px 0 rgba(16,185,129,0.3))",
                    }}
                  />
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.body
        )}
    </span>
  );
}
